import { gql } from "@apollo/client";
import { flatMap, fromPairs, isEqual, pick } from "lodash-es";

export const EVENT_PARAMS_KEYS = ["type", "points", "made", "reboundType", "assistType"];
export const EventParamsFragment = gql`
  fragment EventParamsFragment on Event {
    type
    points
    made
    reboundType
    assistType
  }
`;
export const EVENT_DEFAULT_PARAMS = fromPairs(EVENT_PARAMS_KEYS.map((key) => [key, null]));

const EVENT_TEMPLATES = [
  // TODO: merge miniball into basketball
  ...flatMap(
    [
      ["basketball", "Basketball"],
      ["miniball", "Miniball"],
    ],
    ([sport, wrapperPrefix]) => [
      {
        sport,
        shortName: "1PT",
        title: "1 Point",
        params: {
          type: `${wrapperPrefix}Point`,
          made: true,
          points: 1,
        },
        assistPromptEnabled: true,
        assistPromptWrapper: `${wrapperPrefix}Assist`,
      },
      {
        sport,
        shortName: "2PT",
        title: "2 Point",
        params: {
          type: `${wrapperPrefix}Point`,
          made: true,
          points: 2,
        },
        assistPromptEnabled: true,
        assistPromptWrapper: `${wrapperPrefix}Assist`,
      },
      {
        sport,
        shortName: "3PT",
        title: "3 Point",
        params: {
          type: `${wrapperPrefix}Point`,
          made: true,
          points: 3,
        },
        assistPromptEnabled: true,
        assistPromptWrapper: `${wrapperPrefix}Assist`,
      },
      {
        sport,
        shortName: "AST",
        title: "Assist",
        params: {
          type: `${wrapperPrefix}Assist`,
        },
      },
      {
        sport,
        shortName: "BLK",
        title: "Block",
        params: {
          type: `${wrapperPrefix}Block`,
        },
      },
      {
        sport,
        shortName: "STL",
        title: "Steal",
        params: {
          type: `${wrapperPrefix}Steal`,
        },
      },
      {
        sport,
        shortName: "OR",
        title: "Offensive Rebound",
        params: {
          type: `${wrapperPrefix}Rebound`,
          reboundType: "offensive",
        },
      },
      {
        sport,
        shortName: "DR",
        title: "Defensive Rebound",
        params: {
          type: `${wrapperPrefix}Rebound`,
          reboundType: "defensive",
        },
      },
      {
        sport,
        shortName: "TO",
        title: "Turnover",
        params: {
          type: `${wrapperPrefix}Turnover`,
        },
      },
      {
        sport,
        shortName: "1PM",
        title: "1 Point Miss",
        params: {
          type: `${wrapperPrefix}Point`,
          made: false,
          points: 1,
        },
        reboundPromptEnabled: true,
        reboundPromptWrapper: `${wrapperPrefix}Rebound`,
      },
      {
        sport,
        shortName: "2PM",
        title: "2 Point Miss",
        params: {
          type: `${wrapperPrefix}Point`,
          made: false,
          points: 2,
        },
        reboundPromptEnabled: true,
        reboundPromptWrapper: `${wrapperPrefix}Rebound`,
      },
      {
        sport,
        shortName: "3PM",
        title: "3 Point Miss",
        params: {
          type: `${wrapperPrefix}Point`,
          made: false,
          points: 3,
        },
        reboundPromptEnabled: true,
        reboundPromptWrapper: `${wrapperPrefix}Rebound`,
      },
    ],
  ),
  {
    sport: "futsal",
    shortName: "G",
    title: "Goal",
    params: {
      type: "FutsalPoint",
      made: true,
      points: 1,
    },
    assistPromptEnabled: true,
    assistPromptWrapper: "FutsalAssist",
  },
  {
    sport: "futsal",
    shortName: "M",
    title: "Miss",
    params: {
      type: "FutsalPoint",
      made: false,
      points: 1,
    },
  },
  {
    sport: "futsal",
    shortName: "SAV",
    title: "Save",
    params: {
      type: "FutsalSave",
    },
  },
  {
    sport: "futsal",
    shortName: "AST",
    title: "Assist",
    params: {
      type: "FutsalAssist",
    },
  },
  {
    sport: "netball",
    shortName: "1PT",
    title: "1 Point",
    params: {
      type: "NetballPoint",
      made: true,
      points: 1,
    },
  },
  {
    sport: "netball",
    shortName: "2PT",
    title: "2 Point",
    params: {
      type: "NetballPoint",
      made: true,
      points: 2,
    },
  },
  {
    sport: "netball",
    shortName: "GA",
    title: "Goal Assist",
    params: {
      type: "NetballAssist",
      assistType: "goal_assist",
    },
  },
  {
    sport: "netball",
    shortName: "CR",
    title: "Centre Pass Receive",
    params: {
      type: "NetballAssist",
      assistType: "centre_pass_receive",
    },
  },
  {
    sport: "netball",
    shortName: "FED",
    title: "Feed",
    params: {
      type: "NetballAssist",
      assistType: "feed",
    },
  },
  {
    sport: "netball",
    shortName: "DEF",
    title: "Deflection",
    params: {
      type: "NetballBlock",
    },
  },
  {
    sport: "netball",
    shortName: "INT",
    title: "Intercept",
    params: {
      type: "NetballSteal",
    },
  },
  {
    sport: "netball",
    shortName: "PU",
    title: "Pickup",
    params: {
      type: "NetballSave",
    },
  },
  {
    sport: "netball",
    shortName: "OR",
    title: "Offensive Rebound",
    params: {
      type: "NetballRebound",
      reboundType: "offensive",
    },
  },
  {
    sport: "netball",
    shortName: "DR",
    title: "Defensive Rebound",
    params: {
      type: "NetballRebound",
      reboundType: "defensive",
    },
  },
  {
    sport: "netball",
    shortName: "1PM",
    title: "1 Point Miss",
    params: {
      type: "NetballPoint",
      made: false,
      points: 1,
    },
  },
  {
    sport: "netball",
    shortName: "2PM",
    title: "2 Point Miss",
    params: {
      type: "NetballPoint",
      made: false,
      points: 2,
    },
  },
  {
    sport: "netball",
    shortName: "GPT",
    title: "General Play Turnover",
    params: {
      type: "NetballTurnover",
    },
  },
  {
    sport: "netball",
    shortName: "PEN",
    title: "Penalty",
    params: {
      type: "NetballFoul",
    },
  },
];

export function getEventTemplate(sport, event) {
  return EVENT_TEMPLATES.find(
    (t) =>
      t.sport === sport &&
      isEqual(pick(event, EVENT_PARAMS_KEYS), {
        ...EVENT_DEFAULT_PARAMS,
        ...t.params,
      }),
  );
}

export default EVENT_TEMPLATES;
